import React, { useEffect, useState } from "react";
import { Drawer, Table, Tag, Timeline, Space, Button, message, Spin } from "antd";
import {
  UserOutlined, MailOutlined, PhoneOutlined, ShopOutlined,
  CheckSquareOutlined, RobotOutlined, SendOutlined, CloseOutlined,
  CheckCircleFilled, MinusCircleOutlined, SafetyCertificateOutlined, TagsOutlined,
} from "@ant-design/icons";
import { getPracticeDetail, resendInvite, revokeInvite, verifyUserEmail, reactivatePractice } from "./controller";
import PracticeUsersPanel from "./PracticeUsersPanel";
import PracticeBillingPanel from "./billing/PracticeBillingPanel";
import ContactCarousel from "./ContactCarousel";
import MarkDormantWizard from "./MarkDormantWizard";
import { PracticeTagEditor } from "./LeadTagPicker";
import { tourTag, tourHowFar, TOTAL_STEPS } from "./tourLabel";
import { NOT_APPLICABLE_LABEL, isApplicable, planNote, readinessCounts } from "./lib/readiness";
import "./crm.css";
import "./dormant.css";

// Matches the stage colours used on the CRM list for consistency.
const STAGE_COLOR = {
  not_started: "default", no_owner: "default",
  signed_up: "blue", practice_setup: "geekblue", practice_created: "cyan",
  office_setup: "purple", banking: "gold", inviting_users: "orange",
  subscription: "magenta", sikka_setup: "geekblue", portal_credentials: "volcano",
  procedure_code_defaults: "lime", completed: "green",
};
const INVITE_COLOR = { pending: "gold", accepted: "green", rejected: "red", cancelled: "default" };

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

function initials(name) {
  if (!name) return "—";
  const p = name.trim().split(/\s+/);
  return ((p[0][0] || "") + (p[1] ? p[1][0] : "")).toUpperCase();
}

function Section({ icon, title, subtitle, count, children }) {
  return (
    <div className="crm-section">
      <div className="crm-section-head">
        <span className="crm-section-icon">{icon}</span>
        <span className="crm-section-title">{title}</span>
        {subtitle ? <span className="crm-section-sub">{subtitle}</span> : null}
        {typeof count === "number" ? <span className="crm-section-count">{count}</span> : null}
      </div>
      <div className="crm-section-body">{children}</div>
    </div>
  );
}

export default function PracticeDetailDrawer({ practiceId, onClose, onOpenOffice }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const [dormantOpen, setDormantOpen] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const load = async () => {
    if (!practiceId) return;
    setLoading(true);
    const res = await getPracticeDetail(practiceId);
    setLoading(false);
    if (res.status >= 200 && res.status < 300) {
      setData(res.json);
    } else {
      setData(null);
      message.error("Failed to load practice.");
    }
  };

  useEffect(() => {
    if (practiceId) {
      setData(null);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  const doResend = async (id) => {
    const res = await resendInvite(id);
    if (res.status >= 200 && res.status < 300) {
      message.success("Invite resent.");
      load();
    } else {
      message.error((res.json && res.json.detail) || "Failed to resend.");
    }
  };

  const doRevoke = async (id) => {
    const res = await revokeInvite(id);
    if (res.status >= 200 && res.status < 300) {
      message.success("Invite revoked.");
      load();
    } else {
      message.error("Failed to revoke.");
    }
  };

  const doVerifyEmail = async (userId) => {
    setVerifyBusy(userId);
    const res = await verifyUserEmail(userId);
    setVerifyBusy(null);
    if (res.status >= 200 && res.status < 300) {
      message.success("Email verified.");
      load();
    } else {
      message.error((res.json && res.json.detail) || "Failed to verify email.");
    }
  };

  const doReactivate = async () => {
    setReactivating(true);
    const res = await reactivatePractice(practiceId);
    setReactivating(false);
    if (res.status >= 200 && res.status < 300) {
      message.success("Practice reactivated.");
      load();
    } else {
      message.error((res.json && res.json.detail) || "Couldn't reactivate.");
    }
  };

  const stage = (data && data.onboarding_stage) || {};
  const sub = (data && data.subscription) || {};
  const offices = (data && data.offices) || [];
  // Dormant is per office; the practice is dormant only when every office is.
  // Prefer the server's own verdict, fall back to the offices when it is absent.
  const isDormant = data
    ? typeof data.dormant === "boolean"
      ? data.dormant
      : offices.length > 0 && offices.every((o) => o.dormant)
    : false;
  const contacts = (data && data.contacts) || [];
  const timeline = (data && data.onboarding_timeline) || [];
  const checklist = (data && data.registration_checklist) || [];
  // Counted over applicable items only — steps the practice registered without
  // aren't theirs to finish (see lib/readiness.js).
  const readiness = readinessCounts(checklist);
  const registeredWithout = planNote(data && data.onboarding_plan);
  const tourUsers = (data && data.tour_by_user) || [];
  const invites = (data && data.invites) || [];

  return (
    <Drawer
      closable={false}
      title={null}
      width={828}
      open={!!practiceId}
      onClose={onClose}
      destroyOnClose
      className="crm-drawer"
      bodyStyle={{ padding: 0, background: "#f7f6fb" }}
    >
      <button className="crm-hero-close" onClick={onClose} aria-label="Close"><CloseOutlined /></button>

      {loading || !data ? (
        <div className="crm-drawer-loading"><Spin size="large" /></div>
      ) : (
        <div className="crm-detail">
          {/* ── Hero ─────────────────────────────────────────────── */}
          <div className="crm-hero">
            <div className="crm-hero-top">
              <div className="crm-hero-avatar">{initials(data.name)}</div>
              <div className="crm-hero-id">
                <div className="crm-hero-name">{data.name}</div>
                <div className="crm-hero-owner">
                  <UserOutlined />
                  {data.owner ? `${data.owner.name} · ${data.owner.email}` : "No owner assigned"}
                </div>
              </div>
              {isDormant ? (
                <span className="crmd-pill is-large">Dormant</span>
              ) : (
                <span className={`crm-status-pill ${data.active ? "on" : "off"}`}>
                  {data.active ? "Active" : "Inactive"}
                </span>
              )}
            </div>

            <div className="crm-hero-chips">
              <Tag color={STAGE_COLOR[stage.key] || "default"}>{stage.label || "—"}</Tag>
              {/* Steps switched off in CRM Settings when this practice registered. */}
              {registeredWithout ? <Tag>{registeredWithout}</Tag> : null}
              <Tag color="purple">
                {sub.tier_title
                  ? `${sub.tier_title}${sub.subscription_status ? " · " + sub.subscription_status : ""}`
                  : "No subscription"}
              </Tag>
              {sub.trial ? <Tag color="gold">{`Trial · ${sub.trial.status}`}</Tag> : null}
            </div>

            <div className="crm-hero-contact">
              <span><MailOutlined />{data.email || "—"}</span>
              <span><PhoneOutlined />{data.phone || "—"}</span>
            </div>

            <div className="crmd-hero-actions">
              {isDormant ? (
                <Button size="small" loading={reactivating} onClick={doReactivate}>
                  Reactivate
                </Button>
              ) : (
                <Button size="small" onClick={() => setDormantOpen(true)} disabled={!offices.length}>
                  Mark dormant
                </Button>
              )}
            </div>
          </div>

          {/* ── Quick stats ──────────────────────────────────────── */}
          <div className="crm-stats">
            <div className="crm-stat"><div className="crm-stat-num">{offices.length}</div><div className="crm-stat-label">Offices</div></div>
            <div className="crm-stat"><div className="crm-stat-num">{tourUsers.length}</div><div className="crm-stat-label">Team</div></div>
            <div className="crm-stat"><div className="crm-stat-num">{invites.length}</div><div className="crm-stat-label">Invites</div></div>
          </div>

          {/* ── Offices ──────────────────────────────────────────── */}
          <Section icon={<ShopOutlined />} title="Offices" count={offices.length}>
            <Table
              size="small" rowKey="id" pagination={false}
              locale={{ emptyText: <div className="crm-empty">No offices.</div> }}
              columns={[
                { title: "Name", dataIndex: "name", render: (v) => <span className="crm-name">{v}</span> },
                { title: "PMS", dataIndex: "practice_management_system", render: (v) => v || "—" },
                {
                  title: "Office ID", dataIndex: "office_id",
                  render: (v) => (v ? <code className="crm-code">{v}</code> : <span className="crm-sub">not connected</span>),
                },
              ]}
              dataSource={offices}
            />
          </Section>

          {/* ── Lead tags ────────────────────────────────────────── */}
          <Section icon={<TagsOutlined />} title="Tags" subtitle="On the practice's lead">
            <div style={{ padding: "6px 8px" }}>
              {/* Lazy: the lead is fetched (or created) only when this drawer opens. */}
              <PracticeTagEditor practiceId={practiceId} max={8} size="small" />
            </div>
          </Section>

          {/* ── Contact people (across all offices) ──────────────── */}
          {contacts.length ? (
            <Section icon={<UserOutlined />} title="Contact people" count={contacts.length}>
              <ContactCarousel contacts={contacts} onOpenOffice={onOpenOffice} />
            </Section>
          ) : null}

          {/* ── Registration readiness ───────────────────────────── */}
          <Section
            icon={<CheckSquareOutlined />}
            title="Registration readiness"
            subtitle={readiness.total ? `${readiness.done} of ${readiness.total} complete` : undefined}
          >
            {checklist.length ? (
              <div className="crm-check-list">
                {checklist.map((c) => (
                  <div className={`crm-check-row${isApplicable(c) ? "" : " na"}`} key={c.key}>
                    {!isApplicable(c) ? (
                      <MinusCircleOutlined className="crm-check-icon na" />
                    ) : c.done ? (
                      <CheckCircleFilled className="crm-check-icon done" />
                    ) : (
                      <MinusCircleOutlined className="crm-check-icon todo" />
                    )}
                    <span className={`crm-check-label${c.done && isApplicable(c) ? " done" : ""}`}>{c.label}</span>
                    {!isApplicable(c)
                      ? <span className="crm-check-detail">{NOT_APPLICABLE_LABEL}</span>
                      : c.detail ? <span className="crm-check-detail">{c.detail}</span> : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="crm-empty">No onboarding activity yet.</div>
            )}

            {timeline.length ? (
              <div className="crm-tl-history">
                <button
                  type="button"
                  className="crm-tl-toggle"
                  onClick={() => setShowTimeline((v) => !v)}
                >
                  {showTimeline ? "Hide step history" : "Show step history"}
                </button>
                {showTimeline ? (
                  <Timeline
                    style={{ marginTop: 14 }}
                    items={timeline.map((s) => ({
                      color: s.status === "done" || s.status === "completed" ? "#6B43B5" : "#c9c4d8",
                      children: (
                        <div className="crm-tl-item">
                          <span className="crm-tl-stage">{s.stage || s.name}</span>
                          <span className="crm-tl-date">{fmtDate(s.date_completed)}</span>
                        </div>
                      ),
                    }))}
                  />
                ) : null}
              </div>
            ) : null}
          </Section>

          {/* ── Chatbot onboarding (per user) ────────────────────── */}
          <Section icon={<RobotOutlined />} title="Chatbot onboarding" subtitle="Treatment Planning tour">
            {tourUsers.length ? (
              <div className="crm-user-list">
                {tourUsers.map((r) => {
                  const t = tourTag(r);
                  return (
                    <div className="crm-user-row" key={r.user.id}>
                      <div className="crm-user-id">
                        <div className="crm-user-name">
                          {r.user.name}
                          {r.user.is_owner ? <Tag color="purple" className="crm-owner-tag">Owner</Tag> : null}
                        </div>
                        <div className="crm-sub">{r.user.email}</div>
                        {r.user.email_verified === true ? (
                          <span className="crm-verified"><CheckCircleFilled /> Email verified</span>
                        ) : r.user.email_verified === false ? (
                          <Button
                            className="crm-verify-btn"
                            type="link"
                            size="small"
                            icon={<SafetyCertificateOutlined />}
                            loading={verifyBusy === r.user.id}
                            onClick={() => doVerifyEmail(r.user.id)}
                          >
                            Verify email
                          </Button>
                        ) : null}
                      </div>
                      <div className="crm-user-prog">
                        <Tag color={t.color}>{t.text}</Tag>
                        {r.status !== "not_started" ? (
                          <span className="crm-user-far">{tourHowFar(r)} / {TOTAL_STEPS} steps</span>
                        ) : null}
                      </div>
                      <div className="crm-user-dates">
                        <span><b>Started</b>{fmtDate(r.started_at)}</span>
                        <span><b>Last active</b>{fmtDate(r.last_activity)}</span>
                        <span><b>{r.status === "skipped" ? "Skipped" : "Completed"}</b>{fmtDate(r.completed_at)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="crm-empty">No team members yet.</div>
            )}
          </Section>

          {/* ── Invites ──────────────────────────────────────────── */}
          <Section icon={<SendOutlined />} title="Invites" count={invites.length}>
            <Table
              size="small" rowKey="id" pagination={false}
              locale={{ emptyText: <div className="crm-empty">No invites sent.</div> }}
              columns={[
                { title: "Email", dataIndex: "receiver_email", render: (v) => <span className="crm-name">{v}</span> },
                { title: "State", dataIndex: "state", render: (v) => <Tag color={INVITE_COLOR[v] || "default"}>{v}</Tag> },
                {
                  title: "", key: "a", align: "right",
                  render: (_, r) => (
                    <Space>
                      <Button size="small" disabled={r.is_used} onClick={() => doResend(r.id)}>Resend</Button>
                      <Button size="small" danger disabled={r.is_used || r.state === "cancelled"} onClick={() => doRevoke(r.id)}>Revoke</Button>
                    </Space>
                  ),
                },
              ]}
              dataSource={invites}
            />
          </Section>

          {/* ── Users & Billing ──────────────────────────────────── */}
          <div className="ub-group-title">Users &amp; Billing</div>
          <Section icon={<UserOutlined />} title="Users & office access">
            <PracticeUsersPanel practiceId={practiceId} />
          </Section>
          <PracticeBillingPanel practiceId={data.id || practiceId} practiceName={data.name} onOpenOffice={onOpenOffice} />
        </div>
      )}

      <MarkDormantWizard
        open={dormantOpen}
        target={
          data
            ? {
                kind: "practice",
                id: data.id || practiceId,
                name: data.name,
                offices: offices.map((o) => ({ id: o.id, name: o.name, dormant: !!o.dormant })),
              }
            : null
        }
        onClose={() => setDormantOpen(false)}
        onDone={load}
      />
    </Drawer>
  );
}
